<template>
 <BaseLayout
   title="Top 3 Rankings"
   :customClasses="['top-three-rankings']"
   ref="baseLayout">
   <Form
      @submit.prevent="getTopThreeRankings">
      <strong>Filters</strong>
      <Dropdown
        dropdownPlaceholder="Practice area"
        :hasIcon="true"
        :options="form.practiceAreas"
        customOption="name"
        @select="handlePracticeArea($event)"
        :isDisabled="loading"
        :inputHasLabel="true"
        inputLabelText="Practice Area"
        ref="filterDropdown1" />
      <Dropdown
        dropdownPlaceholder="Practice area keyword"
        :hasIcon="true"
        :options="form.activePracticeArea.keywords || []"
        @select="form.activeKeyword = $event"
        :isDisabled="loading"
        :inputHasLabel="true"
        inputLabelText="Keyword"
        ref="filterDropdown2"
        v-show="Object.keys(form.activePracticeArea).length > 0" />
      <Dropdown
        :options="filteredCompanies"
        customOption="name"
        dropdownPlaceholder="Marketing Company"
        :inputHasLabel="true"
        inputLabelText="Marketing Company"
        :isSearch="true"
        @input="debounceAgencyName($event)"
        ref="filterDropdown3"
        @select="handleMarketingCompany($event)"
        :isDisabled="loading || !canEditMarketingCompany" />
      <Dropdown
        dropdownPlaceholder="Tier"
        :hasIcon="true"
        :options="form.tierOptions"
        @select="form.tier = $event"
        :isDisabled="loading"
        :inputHasLabel="true"
        inputLabelText="Tier"
        ref="filterDropdown4" />
      <Button
        type="submit"
        text="Apply filters"
        :isDisabled="loading" />
    </Form>
    <action-links
        textAlign="right"
        v-if="!loading">
      <Button
        @click="clearFilters"
        text="Clear filters" />
    </action-links>
    <Loading v-show="loading" text="Loading rankings" />
    <Table
      :loading="loading"
      v-if="sortedResults.length > 0">
      <template slot="head">
        <t-row>
          <t-heading>
            #
          </t-heading>
          <t-heading
            @click="sortBy('url')"
            :class="sortedClass('url')">
            Url
          </t-heading>
          <t-heading
            @click="sortBy('rank')"
            :class="sortedClass('rank')">
            Rank
          </t-heading>
          <t-heading
            @click="sortBy('keyword')"
            :class="sortedClass('keyword')">
            Keyword
          </t-heading>
          <t-heading
            @click="sortBy('market_tier')"
            :class="sortedClass('market_tier')">
            Market Tier
          </t-heading>
          <t-heading
            @click="sortBy('last_checked_at')"
            :class="sortedClass('last_checked_at')">
            Date Last Checked
          </t-heading>
          <t-heading
            @click="sortBy('archive_url')"
            :class="sortedClass('archive_url')">
          </t-heading>
        </t-row>
      </template>
      <template slot="body">
        <t-row
          v-for="(result, i) in sortedResults"
          :key="i">
          <t-cell>{{ i + 1 }}</t-cell>
          <t-cell>
            <a
              :href="result.url"
              target="_blank"
              rel="noopener">
              {{ result.url }}
            </a>
          </t-cell>
          <t-cell>{{ result.rank }}</t-cell>
          <t-cell>
            <a
              :href="`https://www.google.com/search?q=${encodeSpaces(result.keyword)}&hl=en`"
              target="_blank">
              {{ result.keyword }}
            </a>
          </t-cell>
          <t-cell>{{ result.market_tier }}</t-cell>
          <t-cell>{{ result.last_checked_at | moment('M/DD/YYYY') }}</t-cell>
          <t-cell>
            <Button
              :hasLink="true"
              :btnLink="{
                name: 'site-history',
                query: {
                  domain: formatDomain(result.url)
                }
              }"
              :isView="true" />
          </t-cell>
        </t-row>
      </template>
    </Table>
    <p v-else-if="!loading && sortedResults.length === 0">
      No results. Try changing or clearing filters.
    </p>
  </BaseLayout>
</template>

<script>
import Table from '@/components/table/Table'
import TableRow from '@/components/table/TableRow'
import TableDataCell from '@/components/table/TableDataCell'
import TableHeading from '@/components/table/TableHeading'
import Dropdown from '@/components/form/Dropdown'
import Button from '@/components/form/Button'
import Form from '@/components/form/Form'
import BaseLayout from '@/views/BaseLayout'
import Loading from '@/components/misc/Loading'
import ActionLinks from '@/components/misc/ActionLinks'
import { mapState } from 'vuex'
import _ from 'lodash'

export default {
  name: 'ScoreTopThree',
  components: {
    Table,
    Button,
    Loading,
    BaseLayout,
    Dropdown,
    Form,
    ActionLinks,
    't-row': TableRow,
    't-cell': TableDataCell,
    't-heading': TableHeading
  },
  data () {
    return {
      loading: false,
      filteredCompanies: [],
      sort: {
        key: 'rank',
        isAsc: true
      },
      form: {
        activeMarketingCompany: {},
        tierOptions: [1, 2, 3, 4, 5],
        tier: '',
        practiceAreas: [],
        activePracticeArea: {},
        activeKeyword: ''
      },
      topThreeRankings: [],
      defaultMarketingCompany: 'iLawyerMarketing',
      canEditMarketingCompany: false
    }
  },
  computed: {
    ...mapState({
      formData: state => state.form
    }),

    sortedResults () {
      const list = this.topThreeRankings ? this.topThreeRankings : []
      if (this.sort.key) {
        list.sort((a, b) => {
          a = _.get(a, this.sort.key)
          b = _.get(b, this.sort.key)
          return (a === b ? 0 : a > b ? 1 : -1) * (this.sort.isAsc ? 1 : -1)
        })
      }
      return list
    }
  },
  mounted () {
    this.getPracticeAreas()
    this.setDefaultMarketingCompany()
    this.getAndSetPermissions()
  },
  methods: {
    sortedClass (key) {
      return this.sort.key === key
        ? `sorted ${this.sort.isAsc ? 'asc' : 'desc'}`
        : ''
    },

    encodeSpaces (str) {
      return str.replace(/ /g, '+')
    },

    debounceAgencyName: _.debounce(function (val) {
      if (val.length > 2) {
        this.form.agencyName = val
        this.getMarketingCompanies(val)
      } else {
        this.filteredCompanies = []
      }
    }, 200),

    sortBy (key) {
      this.sort.isAsc = this.sort.key === key ? !this.sort.isAsc : false
      this.sort.key = key
    },

    getAndSetPermissions () {
      this.$arc.get('users/me')
        .then(res => {
          if (res.data.name === 'Mike Perez') {
            this.canEditMarketingCompany = true
          } else {
            this.canEditMarketingCompany = false
          }
        }).catch(err => {
          console.error(err)
        })
    },

    setDefaultMarketingCompany () {
      this.$arc.get('marketing_companies', {
        params: {
          name_like: this.defaultMarketingCompany
        }
      }).then(res => {
        if (res.data.marketing_companies.length > 0) {
          this.form.activeMarketingCompany = res.data.marketing_companies[0]
          this.$refs.filterDropdown3.selected = this.form.activeMarketingCompany.name
        }
      }).catch(err => {
        console.error(err)
      })
    },

    getPracticeAreas () {
      this.$arc.get('practice_areas')
        .then(res => {
          this.form.practiceAreas = res.data.practice_areas

          if (this.form.practiceAreas.length > 0) {
            this.form.practiceAreas.forEach(pa => {
              pa.keywords = pa.keywords ? Object.entries(pa.keywords).map(k => k[0]) : []
            })
          }
        }).catch(err => {
          console.error(err)
        })
    },

    getTopThreeRankings () {
      this.loading = true
      console.log(this.loading)
      this.$arc.get(`practice_areas/${this.form.activePracticeArea.id}/top_3_rankings`, {
        params: {
          ...(this.form.activeKeyword ? { keyword: this.form.activeKeyword } : {}),
          ...(this.form.activeMarketingCompany.id ? { marketing_company_id: this.form.activeMarketingCompany.id } : {}),
          ...(this.form.tier ? { market_tier: this.form.tier } : {}),
          last_n_days: 30
        }
      }).then(res => {
        console.log(res)
        this.topThreeRankings = res.data.top_3_rankings
        this.loading = false
        console.log(this.loading)
      }).catch(err => {
        console.error(err)
        this.loading = false
        console.log(this.loading)
      })
    },

    getMarketingCompanies (name) {
      this.$arc.get('marketing_companies?per_page=1000&strict=0', {
        params: {
          ...(name ? { name_like: name } : {})
        }
      })
        .then(res => {
          this.filteredCompanies = res.data.marketing_companies
          // this.getPagedResults()
        })
        .catch(err => {
          console.error(err)
        })
    },

    handlePracticeArea (evt) {
      if (evt) {
        const selected = this.form.practiceAreas.find(pa => pa.name === evt)
        if (selected) {
          this.form.activePracticeArea = selected
        } else {
          this.form.activePracticeArea = {}
        }
      }
    },

    handleMarketingCompany (evt) {
      if (evt) {
        const selected = this.filteredCompanies.find(mc => mc.name === evt)
        if (selected) {
          this.form.activeMarketingCompany = selected
        } else {
          this.form.activeMarketingCompany = {}
        }
      }
    },

    clearFilters () {
      this.form.activePracticeArea = {}
      this.form.tier = ''
      this.form.activeKeyword = ''
      this.$refs.filterDropdown1.selected = ''
      this.$refs.filterDropdown2.selected = ''
      if (this.canEditMarketingCompany) this.$refs.filterDropdown3.selected = ''
      this.$refs.filterDropdown4.selected = ''
    },

    formatDomain (domain) {
      return domain.replace('http://', '').replace('https://', '').replace('www.', '').split(/[/?#]/)[0]
    }
  }
}
</script>

<style lang="scss">
.top-three-rankings {
  form {
    align-items: flex-end;
    margin-bottom: 30px;
    justify-content: flex-start;
    strong {
      margin-bottom: 1rem;
    }
    > div {
      // flex-basis: calc((100% / 4) - 30px) !important;
      &.flex {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        label {
          font-weight: 600;
          margin-bottom: 10px;
          display: block;
          width: 100%;
        }
        .input-container {
          flex-basis: calc((100% / 2) - 7px);
        }
      }
    }
    strong {
      margin-right: 15px;
    }
  }
  .logo-container {
    img {
      margin: 0 1rem;
      vertical-align: bottom;
      display: inline-block;
    }
  }
}
</style>
